import React, {
  FunctionComponent,
  PropsWithChildren,
  useMemo,
  useState
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  ButtonStandard,
  ButtonStandardType
} from '../../button/ButtonStandard/ButtonStandard';
import {
  GlobalConfirmDeleteDialogContext,
  GlobalConfirmDeleteDialogContextOpenOptions,
  GlobalConfirmDeleteDialogContextValue
} from '../../context/GlobalConfirmDeleteDialogContext';
import { useEventCallback } from '../../hooks/useEventCallback';
import { useModalOpened } from '../../hooks/useModalOpened/useModalOpened';
import { prepareProps } from '../../utils/propUtils';
import {
  ConfirmModal,
  ConfirmModalWidthType
} from '../ConfirmModal/ConfirmModal';
import { ConfirmModalBody } from '../ConfirmModalBody/ConfirmModalBody';
import { ConfirmModalButtonFooter } from '../ConfirmModalButtonFooter/ConfirmModalButtonFooter';
import { ConfirmModalHeader } from '../ConfirmModalHeader/ConfirmModalHeader';

export type GlobalConfirmDeleteDialogProviderProps = PropsWithChildren<
  Record<never, never>
>;

export const GlobalConfirmDeleteDialogProvider: FunctionComponent<GlobalConfirmDeleteDialogProviderProps> =
  React.memo((props) => {
    const { children } = prepareProps({ props });

    const [options, setOptions] =
      useState<GlobalConfirmDeleteDialogContextOpenOptions<unknown> | null>(
        null
      );
    const {
      open: openModal,
      close: closeModal,
      modalProps
    } = useModalOpened({ closeOnBackDropClick: true });
    const { t } = useTranslation('record-it-components');

    const open: GlobalConfirmDeleteDialogContextValue['open'] =
      useEventCallback((opts) => {
        setOptions(
          opts as GlobalConfirmDeleteDialogContextOpenOptions<unknown>
        );
        openModal();
      });

    const handleDeleteClick = useEventCallback(() => {
      options?.onDelete(options.item);
      closeModal();
    });

    const contextValue: GlobalConfirmDeleteDialogContextValue = useMemo(() => {
      return {
        open
      };
    }, [open]);

    return (
      <GlobalConfirmDeleteDialogContext.Provider value={contextValue}>
        {children}
        <ConfirmModal
          {...modalProps}
          widthType={ConfirmModalWidthType.Auto}
          data-testid="GlobalConfirmDialogProvider.Modal"
        >
          <ConfirmModalHeader>
            {t('modal.GlobalConfirmDeleteDialogProvider.title')}
          </ConfirmModalHeader>
          <ConfirmModalBody>
            {t('modal.GlobalConfirmDeleteDialogProvider.message', {
              name: options?.getName(options.item)
            })}
          </ConfirmModalBody>
          <ConfirmModalButtonFooter>
            <ButtonStandard
              type={ButtonStandardType.Danger}
              onClick={handleDeleteClick}
              data-testid="GlobalConfirmDialogProvider.DeleteButton"
            >
              {t('modal.GlobalConfirmDeleteDialogProvider.delete')}
            </ButtonStandard>
            <ButtonStandard
              type={ButtonStandardType.Secondary}
              onClick={closeModal}
              data-testid="GlobalConfirmDialogProvider.CancelButton"
            >
              {t('modal.GlobalConfirmDeleteDialogProvider.cancel')}
            </ButtonStandard>
          </ConfirmModalButtonFooter>
        </ConfirmModal>
      </GlobalConfirmDeleteDialogContext.Provider>
    );
  });
